import type { Action, ActionValidationResult } from '../types/actions';
import { isValidSelector } from '../utils/selector-utils';

export class ActionValidator {
  /**
   * Validate an action before execution
   */
  validate(action: Action): ActionValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Validate based on action type
    switch (action.type) {
      case 'click':
        this.validateClickAction(action, errors, warnings);
        break;
      case 'fill':
        this.validateFillAction(action, errors, warnings);
        break;
      case 'navigate':
        this.validateNavigateAction(action, errors, warnings);
        break;
      case 'submit':
        this.validateSubmitAction(action, errors, warnings);
        break;
      case 'scroll':
        this.validateScrollAction(action, errors, warnings);
        break;
      case 'select':
        this.validateSelectAction(action, errors, warnings);
        break;
      case 'hover':
      case 'focus':
      case 'blur':
        this.validateBasicAction(action, errors, warnings);
        break;
      default:
        errors.push(`Unknown action type: ${(action as any).type}`);
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings
    };
  }

  private validateBasicAction(action: any, errors: string[], _warnings: string[]): void {
    if (!action.selector) {
      errors.push('Selector is required');
    } else if (!isValidSelector(action.selector)) {
      errors.push(`Invalid selector: ${action.selector}`);
    }
  }

  private validateClickAction(action: any, errors: string[], warnings: string[]): void {
    this.validateBasicAction(action, errors, warnings);
  }

  private validateFillAction(action: any, errors: string[], warnings: string[]): void {
    this.validateBasicAction(action, errors, warnings);

    if (action.value === undefined || action.value === null) {
      errors.push('Value is required for fill action');
    }

    if (typeof action.value !== 'string' && typeof action.value !== 'number') {
      errors.push('Value must be a string or number');
    }
  }

  private validateNavigateAction(action: any, errors: string[], warnings: string[]): void {
    if (!action.url) {
      errors.push('URL is required for navigate action');
      return;
    }

    // Basic URL validation
    try {
      new URL(action.url, window.location.origin);
    } catch {
      errors.push(`Invalid URL: ${action.url}`);
    }

    // Warn about external navigation
    try {
      const targetUrl = new URL(action.url, window.location.origin);
      if (targetUrl.origin !== window.location.origin) {
        warnings.push('Navigating to external domain');
      }
    } catch {
      // Already handled in try block above
    }
  }

  private validateSubmitAction(action: any, errors: string[], warnings: string[]): void {
    this.validateBasicAction(action, errors, warnings);

    // Warn if form might navigate away
    if (action.waitForNavigation === undefined) {
      warnings.push('Consider setting waitForNavigation for submit actions');
    }
  }

  private validateScrollAction(action: any, errors: string[], _warnings: string[]): void {
    // Scroll can work with or without selector
    if (action.selector && !isValidSelector(action.selector)) {
      errors.push(`Invalid selector: ${action.selector}`);
    }

    // Must have either selector or x/y coordinates
    if (!action.selector && action.x === undefined && action.y === undefined) {
      errors.push('Scroll action requires either selector or x/y coordinates');
    }
  }

  private validateSelectAction(action: any, errors: string[], _warnings: string[]): void {
    this.validateBasicAction(action, errors, _warnings);

    if (!action.value && action.value !== 0) {
      errors.push('Value is required for select action');
    }
  }
}
